import { FC, useEffect, useRef } from "react";
//#region types
import { BenefitFrequency } from "models/enums/benefits";
import { BenefitMonthsSelectorProps } from "models/propTypes/benefitsPropTypes";
import { MonthInputProps } from "models/propTypes/wintFormPropsTypes";
import { StyleEnums } from "models/enums";
import { TranslationKeys } from "translations";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { Description, Label, VerticalFormField } from "components/common/wintForms";
import MonthInput from "components/common/wintForms/formFields/monthInput";
import { useFloatingMessages } from "components/common";
//#endregion
import { DateHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

const BenefitMonthsSelector: FC<BenefitMonthsSelectorProps> = (props) => {
  const { connect, frequency, mainLabel } = props;
  const { enqueueMessage, closeMessage } = useFloatingMessages();
  const messageKeyRef = useRef<string | number | undefined>(undefined);

  const currentYearStartDate = DateHelper.getFirstDayOfYear(DateHelper.getYear(DateHelper.today()));

  const startDateProps = connect("startDate");
  const startDateValue = startDateProps.value;

  useEffect(() => {
    if (startDateValue) {
      const currentMonth = DateHelper.getFirstDayOfMonth(DateHelper.today());
      const selectedMonth = DateHelper.getFirstDayOfMonth(new Date(startDateValue));

      if (selectedMonth < currentMonth && !messageKeyRef.current) {
        const key = enqueueMessage(
          LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Benefit.RetroactiveBenefitWarning),
          StyleEnums.DisplayType.Warning,
          false
        );
        messageKeyRef.current = key;
      }

      if (selectedMonth >= currentMonth) {
        if (messageKeyRef.current) {
          closeMessage(messageKeyRef.current);
          messageKeyRef.current = undefined;
        }
      }
    }

    return () => {
      if (messageKeyRef.current) {
        closeMessage(messageKeyRef.current);
        messageKeyRef.current = undefined;
      }
    };
  }, [startDateValue, enqueueMessage, closeMessage]);

  if (frequency === BenefitFrequency.Monthly) {
    return (
      <>
        {mainLabel && <Label className={cssExports.formLabelWithMarginTop}>{mainLabel}</Label>}
        <Grid
          item
          container
          direction="row"
          spacing={1}
          className={mainLabel ? cssExports.gridContainerRemoveItemPadding : ""}
        >
          <Grid item xs={6}>
            <VerticalFormField<MonthInputProps>
              Component={MonthInput}
              {...startDateProps}
              label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.StartDate)}
              popperPositionFixed
              minDate={currentYearStartDate}
            />
          </Grid>
          <Grid item xs={6}>
            <VerticalFormField<MonthInputProps>
              Component={MonthInput}
              {...connect("endDate")}
              label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.EndDate)}
              popperPositionFixed
            />
          </Grid>
        </Grid>
        <Description>
          {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Benefit.MonthSelectorDescription)}
        </Description>
      </>
    );
  }

  return (
    <VerticalFormField<MonthInputProps>
      Component={MonthInput}
      label={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.MonthPascal)}
      {...startDateProps}
      popperPositionFixed
    />
  );
};

export default BenefitMonthsSelector;
